import React, { Component } from 'react'
import { Trans } from '@lingui/react'
import { connect } from 'react-redux'
import compose from 'lodash/fp/compose'

import errorBoundary, {
  ErrorBoundaryProps,
} from 'client/shared/decorators/error-boundary'
import urlFor, { QueryParams } from 'shared/tools/url-helper'

import Spacer from 'client/shared/blocks/spacer'
import Shelf from 'client/bookmate/blocks/shelf'
import List, { HeaderRank } from 'client/bookmate/blocks/list'
import Linka from 'client/shared/blocks/linka'
import MetaCounters from 'client/bookmate/blocks/meta-counters'
import TextTruncate from 'client/shared/blocks/text-truncate'
import MoreButton from 'client/shared/blocks/more-button'

import { getBookById } from 'client/bookmate/selectors/book-selector'
import { getShelvesByIds } from 'client/bookmate/reducers/shelves-reducer'

import { ShelfProps } from 'client/shared/types/shelf'
import { BookSerialProps } from 'client/shared/types/resource'
import { State } from 'shared/types/redux'

type Props = {
  shelves: ShelfProps[]
  resource: BookSerialProps
  currentUserLogin: string
  type?: 'default' | 'small'
  resourceType: 'serial' | 'book'
  query?: QueryParams
  headerRank: HeaderRank
} & ErrorBoundaryProps

class BookShelvesIndexBox extends Component<Props> {
  getListHeaderRank() {
    return this.props.type === 'small' ? 2 : 1
  }
  getSpacerSize() {
    return this.props.type === 'small' ? 32 : 48
  }

  render() {
    const {
      type,
      shelves,
      resource,
      resourceType = 'book',
      headerRank,
    } = this.props

    if (shelves.length === 0) return null

    return (
      <div>
        <List
          header="book.on_shelves"
          headerRank={headerRank || this.getListHeaderRank()}
        >
          {shelves.slice(0, 5).map((shelf, i) => {
            return type === 'small'
              ? this.renderPseudoShelf(shelf, i)
              : this.renderShelf(shelf, i)
          })}
        </List>
        <Spacer size={24} />
        <MoreButton
          path={urlFor[`${resourceType}Shelves`](resource.uuid)}
          centered
        >
          <Trans id="topic.show_all" />
        </MoreButton>
        <Spacer size={this.getSpacerSize()} />
      </div>
    )
  }

  renderShelf(shelf, key) {
    const { currentUserLogin, query } = this.props

    return (
      <Shelf
        query={query}
        shelf={shelf}
        kind="list"
        key={key}
        as="li"
        showPlus={shelf.creator.login !== currentUserLogin}
      />
    )
  }

  renderPseudoShelf(shelf, key) {
    const pseudoShelfStyle = {
      display: 'flex',
      boxSizing: 'border-box',
      minHeight: '40px',
      padding: '8px 0',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '14px',
      lineHeight: '16px',
      borderBottom: '1px solid #e7e7e7',
      fontWeight: 'bold',
    }

    return (
      <div key={key}>
        <Linka path={urlFor.shelf(shelf.uuid, this.props.query)}>
          <span style={pseudoShelfStyle}>
            <TextTruncate lines={2} text={shelf.title} textType="title" />
            {shelf.books_count > 0 && (
              <span>
                <MetaCounters
                  counters={[{ kind: 'books', count: shelf.books_count }]}
                />
              </span>
            )}
          </span>
        </Linka>
      </div>
    )
  }
}

const connectWrapper = connect((state: State, { resourceType }) => ({
  shelves: getShelvesByIds(state, state[resourceType].shelves),
  resource: getBookById(state, state[resourceType].uuid),
  currentUserLogin: state.currentUser.data.login,
  query: state.app.storedQuery,
}))

const wrappers = compose(connectWrapper, errorBoundary)

export default wrappers(BookShelvesIndexBox)
